/**
 * Bars with rounded top corners.
 *
 * Chart.js only supports `borderRadius` on bars from v3 onwards; this project
 * is on 2.9.4, so the only way is to extend the Rectangle element and register
 * a custom controller that uses it as its dataElementType.
 *
 * The radius is read from `options.barRadius` (Chart.js ignores keys it does
 * not know). With 0 — or when the bar has a border, or is horizontal — it
 * falls back to the original draw so cases this path does not cover stay
 * untouched.
 */
import Chart from 'chart.js'
import { generateChart } from 'vue-chartjs'

const isVertical = (vm) => !vm.horizontal

// equivalent to Chart.js' internal boundingRects, which is not exported
const barRect = (vm) => {
  const half = vm.width / 2

  return {
    x: vm.x - half,
    y: Math.min(vm.y, vm.base),
    w: vm.width,
    h: Math.abs(vm.base - vm.y)
  }
}

const RoundedRectangle = Chart.elements.Rectangle.extend({
  /**
   * On a stacked chart only the topmost segment gets rounded corners: if every
   * segment had them you would see curves in the middle of the stack.
   */
  isTopOfStack () {
    const chart = this._chart
    const yAxes = ((chart.options.scales || {}).yAxes || [])[0] || {}

    if (!yAxes.stacked) {
      return true
    }

    const datasets = chart.data.datasets || []
    const own = (datasets[this._datasetIndex].data || [])[this._index]
    const ownSign = own < 0 ? -1 : 1

    for (let i = datasets.length - 1; i > this._datasetIndex; i--) {
      // datasets turned into a line (finalAsCompute) are not bars
      if (datasets[i].type && datasets[i].type !== 'bar') {
        continue
      }

      if (!chart.isDatasetVisible(i)) {
        continue
      }

      const value = (datasets[i].data || [])[this._index]

      // negative values stack the other way, so only same sign values
      // compete for the outer edge
      if (value && (value < 0 ? -1 : 1) === ownSign) {
        return false
      }
    }

    return true
  },
  draw () {
    const vm = this._view
    const radius = this._chart.options.barRadius || 0

    // cases this draw does not cover: bordered bars, horizontal bars, no
    // radius, or segments that are not the top of a stack
    if (!radius || !isVertical(vm) || vm.borderWidth || !this.isTopOfStack()) {
      return Chart.elements.Rectangle.prototype.draw.apply(this, arguments)
    }

    const ctx = this._chart.ctx
    const { x, y, w, h } = barRect(vm)
    const r = Math.min(radius, w / 2, h)

    // negative bar: the free end is the bottom edge
    const inverted = vm.base < vm.y
    const top = inverted ? y + h : y
    const dir = inverted ? -1 : 1

    ctx.fillStyle = vm.backgroundColor
    ctx.beginPath()
    ctx.moveTo(x, top + h * dir)
    ctx.lineTo(x, top + r * dir)
    ctx.quadraticCurveTo(x, top, x + r, top)
    ctx.lineTo(x + w - r, top)
    ctx.quadraticCurveTo(x + w, top, x + w, top + r * dir)
    ctx.lineTo(x + w, top + h * dir)
    ctx.closePath()
    ctx.fill()
  }
})

Chart.defaults.roundedBar = Chart.helpers.clone(Chart.defaults.bar)

// dataset level defaults resolve through chart.options.datasets[_type], which
// here becomes 'roundedBar': without this copy categoryPercentage and
// barPercentage are lost and bar widths come out miscalculated
Chart.defaults.global.datasets.roundedBar = Chart.helpers.clone(
  Chart.defaults.global.datasets.bar
)

Chart.controllers.roundedBar = Chart.controllers.bar.extend({
  dataElementType: RoundedRectangle
})

export default generateChart('rounded-bar', 'roundedBar')
